import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../api';
import { usePermissions } from '../../hooks/usePermissions';
import { Layout } from '../../components/layout/Layout';
import { Checkbox } from '../../components/ui/checkbox';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import BulkEditBar from '../../components/BulkEditBar';
import ExportButton from '../../components/ExportButton';
import { 
  Projector, Plus, Search, Eye, Edit, Trash2, Copy, X,
  Building2, MapPin, ChevronUp, ChevronDown, ChevronsUpDown
} from 'lucide-react';

const Proyectores = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions('micro', 'proyectores');
  
  // Data states
  const [proyectores, setProyectores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ 
    total: 0, 
    active: 0, 
    inactive: 0, 
    sin_estado: 0,
    by_brand: []
  });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 0 });
  const [sortConfig, setSortConfig] = useState({ field: null, dir: 'asc' });
  
  // Filter states
  const [filters, setFilters] = useState({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', location_filter: '' });
  const [filterOptions, setFilterOptions] = useState({ brands: [], statuses: [], sites: [], empresas: [] });
  const [formOptions, setFormOptions] = useState({ empresas: [], sites: [], brands: [], statuses: [], ubicaciones: [], proveedores: [], clientes: [] });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProyector, setSelectedProyector] = useState(null);
  const [formData, setFormData] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [proyectorToDelete, setProyectorToDelete] = useState(null);
  const [activeStatFilter, setActiveStatFilter] = useState(null);

  // Bulk selection
  const { 
    selectedIds, selectedCount, isAllSelected, isSomeSelected, 
    isSelected, toggleRow, toggleAll, clearSelection 
  } = useBulkSelection(proyectores);

  // Fetch functions
  const fetchStats = async () => {
    try {
      const response = await api.get('/assets/proyectores/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/assets/proyectores/filter/options');
      setFilterOptions(response.data);
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const fetchFormOptions = async () => {
    try {
      const response = await api.get('/assets/proyectores/form/options');
      setFormOptions(response.data);
    } catch (error) {
      console.error('Error fetching form options:', error);
    }
  };

  const fetchProyectores = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      params.append('limit', pagination.limit);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.brand_id) params.append('brand_id', filters.brand_id);
      if (filters.status_id) params.append('status_id', filters.status_id);
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (filters.cliente_id) params.append('cliente_id', filters.cliente_id);
      if (filters.asset_tag_filter) params.append('asset_tag_filter', filters.asset_tag_filter);
      if (filters.modelo_filter) params.append('modelo_filter', filters.modelo_filter);
      if (filters.ip_filter) params.append('ip_filter', filters.ip_filter);
      if (filters.location_filter) params.append('location_filter', filters.location_filter);
      if (sortConfig.field) {
        params.append('sort_by', sortConfig.field);
        params.append('sort_dir', sortConfig.dir);
      }
      
      const response = await api.get(`/assets/proyectores?${params.toString()}`);
      setProyectores(response.data.data);
      setPagination(prev => ({ 
        ...prev, 
        total: response.data.total, 
        pages: response.data.pages 
      }));
    } catch (error) {
      console.error('Error fetching proyectores:', error);
      toast.error('Error al cargar proyectores');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
    fetchFormOptions();
  }, []);

  useEffect(() => {
    fetchProyectores();
  }, [pagination.page, pagination.limit, sortConfig]);

  // Debounced filter fetch (800ms)
  useEffect(() => {
    const t = setTimeout(() => {
      setPagination(prev => ({ ...prev, page: 1 }));
      fetchProyectores();
    }, 800);
    return () => clearTimeout(t);
  }, [filters]);

  // Handlers
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ search: '', brand_id: '', status_id: '', site_id: '', cliente_id: '', asset_tag_filter: '', modelo_filter: '', ip_filter: '', location_filter: '' });
    setActiveStatFilter(null);
  };

  const hasActiveFilters = Object.values(filters).some(v => v && v !== '');

  const handleStatClick = (filterType) => {
    if (activeStatFilter === filterType) {
      clearFilters();
    } else {
      setActiveStatFilter(filterType);
      if (filterType === 'total') {
        clearFilters();
      } else if (filterType === 'active') {
        setFilters(prev => ({ ...prev, status_id: '6' }));
      } else if (filterType === 'inactive') {
        setFilters(prev => ({ ...prev, status_id: '7' }));
      } else if (filterType === 'sin_estado') {
        setFilters(prev => ({ ...prev, status_id: 'null' }));
      }
    }
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      dir: prev.field === field && prev.dir === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.field !== field) {
      return <ChevronsUpDown className="h-3 w-3 text-muted-foreground/50" />;
    }
    return sortConfig.dir === 'asc' 
      ? <ChevronUp className="h-3 w-3 text-primary" />
      : <ChevronDown className="h-3 w-3 text-primary" />;
  };

  const openCreateModal = () => {
    setModalMode('create');
    setFormData({});
    setShowModal(true);
  };

  const openEditModal = (proyector) => {
    setModalMode('edit');
    setSelectedProyector(proyector);
    setFormData({
      empresa_id: proyector.empresa_id || '',
      site_id: proyector.site_id || '',
      brand_id: proyector.brand_id || '',
      asset_tag: proyector.asset_tag || '',
      serial_number: proyector.serial_number || '',
      modelo: proyector.modelo || '',
      ip_address: proyector.ip_address || '',
      ubicacion: proyector.ubicacion || '',
      location_id: proyector.location_id || '',
      status_id: proyector.status_id || '',
      supplier_id: proyector.supplier_id || '',
      cliente_id: proyector.cliente_id || '',
      resolution: proyector.resolution || '',
      purchase_date: proyector.purchase_date || '',
      comments: proyector.comments || ''
    });
    setShowModal(true);
  };

  const openCopyModal = (proyector) => {
    setModalMode('create');
    setFormData({
      empresa_id: proyector.empresa_id || '',
      site_id: proyector.site_id || '',
      brand_id: proyector.brand_id || '',
      modelo: proyector.modelo || '',
      ubicacion: proyector.ubicacion || '',
      location_id: proyector.location_id || '',
      status_id: proyector.status_id || '',
      supplier_id: proyector.supplier_id || '',
      resolution: proyector.resolution || ''
      // Campos únicos quedan vacíos: asset_tag, serial_number, ip_address
    });
    setShowModal(true);
    toast.info('Datos copiados - Complete los campos únicos (Asset Tag, Serial, IP)');
  };

  const handleSubmit = async () => {
    if (!formData.asset_tag) {
      toast.error('El Asset Tag es requerido');
      return;
    }
    if (!formData.empresa_id) {
      toast.error('La Empresa es requerida');
      return;
    }
    if (!formData.site_id) {
      toast.error('El Site es requerido');
      return;
    }

    try {
      if (modalMode === 'create') {
        await api.post('/assets/proyectores', formData);
        toast.success('Proyector creado exitosamente');
      } else {
        await api.put(`/assets/proyectores/${selectedProyector.id}`, formData);
        toast.success('Proyector actualizado exitosamente');
      }
      setShowModal(false);
      fetchProyectores();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/assets/proyectores/${proyectorToDelete.id}`);
      toast.success('Proyector eliminado exitosamente');
      setShowDeleteModal(false);
      setProyectorToDelete(null);
      fetchProyectores();
      fetchStats();
      fetchFilterOptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar');
    }
  };

  const getStatusBadge = (status) => {
    if (!status) return <span className="badge badge-default">Sin estado</span>;
    if (status.toLowerCase().includes('activo')) return <span className="badge badge-green">{status}</span>;
    if (status.toLowerCase().includes('inactivo')) return <span className="badge badge-red">{status}</span>;
    return <span className="badge badge-default">{status}</span>;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Projector className="h-8 w-8 text-primary" strokeWidth={1.5} />
            <div>
              <h1 className="text-2xl font-semibold">Proyectores</h1>
              <p className="text-sm text-muted-foreground">Gestión de proyectores corporativos</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <ExportButton 
              endpoint="/assets/proyectores"
              filters={{ search: filters.search }}
              filename="proyectores"
            />
            {canCreate && (
              <Button onClick={openCreateModal} data-testid="new-proyector-btn">
                <Plus className="h-4 w-4 mr-2" strokeWidth={1.5} />
                Nuevo Proyector
              </Button>
            )}
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="proyector-stats">
          {/* Total */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'total' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleStatClick('total')}
            data-testid="stats-total"
          >
            <div className="mb-2">
              <Projector className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value">{stats.total}</div>
            <div className="stats-card-label">Total</div>
          </div>
          
          {/* Activos */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'active' ? 'ring-2 ring-green-500' : ''}`}
            onClick={() => handleStatClick('active')}
            data-testid="stats-active"
          >
            <div className="mb-2">
              <Projector className="h-8 w-8 text-green-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-green-500">{stats.active}</div>
            <div className="stats-card-label">Activos</div>
          </div>
          
          {/* Inactivos */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'inactive' ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleStatClick('inactive')}
            data-testid="stats-inactive"
          >
            <div className="mb-2">
              <Projector className="h-8 w-8 text-red-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-red-500">{stats.inactive}</div>
            <div className="stats-card-label">Inactivos</div>
          </div>
          
          {/* Sin Estado */}
          <div 
            className={`stats-card cursor-pointer transition-all ${activeStatFilter === 'sin_estado' ? 'ring-2 ring-yellow-500' : ''}`}
            onClick={() => handleStatClick('sin_estado')}
            data-testid="stats-sin-estado"
          >
            <div className="mb-2">
              <Projector className="h-8 w-8 text-yellow-500" strokeWidth={1.5} />
            </div>
            <div className="stats-card-value text-yellow-500">{stats.sin_estado}</div>
            <div className="stats-card-label">Sin Estado</div>
          </div>
          
          {/* Top Marca */}
          <div className="stats-card flex items-center gap-3" data-testid="stats-brand">
            <Building2 className="h-6 w-6 text-purple-500 flex-shrink-0" strokeWidth={1.5} />
            <div>
              <div className="stats-card-value text-purple-500">
              {stats.by_brand?.[0]?.count || 0}
            </div>
              <div className="stats-card-label">{stats.by_brand?.[0]?.name || 'Sin Marca'}</div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <Card className="card-subtle">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                <Input
                  placeholder="Buscar en todos los campos..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10"
                  data-testid="global-search-input"
                />
              </div>
              {hasActiveFilters && (
                <Button variant="outline" size="sm" onClick={clearFilters} data-testid="clear-filters-btn">
                  <X className="h-4 w-4 mr-1" strokeWidth={1.5} />
                  Limpiar filtros
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="card-subtle">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="proyectores-table">
                <thead>
                  <tr className="border-b border-border">
                    <th className="w-10 py-3 px-4">
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isSomeSelected}
                        onCheckedChange={toggleAll}
                        aria-label="Seleccionar todos"
                      />
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('asset_tag')}
                    >
                      <div className="flex items-center gap-1">
                        ASSET TAG <SortIcon field="asset_tag" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('brand_name')}>
                      <div className="flex items-center gap-1">MARCA <SortIcon field="brand_name" /></div>
                    </th>
                    <th 
                      className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground"
                      onClick={() => handleSort('modelo')}
                    >
                      <div className="flex items-center gap-1">
                        MODELO <SortIcon field="modelo" />
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('cliente_name')}>
                      <div className="flex items-center gap-1">CLIENTE <SortIcon field="cliente_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('ip_address')}>
                      <div className="flex items-center gap-1">IP <SortIcon field="ip_address" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground" onClick={() => handleSort('status_name')}>
                      <div className="flex items-center gap-1">ESTADO <SortIcon field="status_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('site_name')}>
                      <div className="flex items-center gap-1">SITE <SortIcon field="site_name" /></div>
                    </th>
                    <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground hidden lg:table-cell cursor-pointer hover:text-foreground" onClick={() => handleSort('location_name')}>
                      <div className="flex items-center gap-1">UBICACIÓN <SortIcon field="location_name" /></div>
                    </th>
                    <th className="text-right py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground w-28">ACCIONES</th>
                  </tr>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="w-10 py-2 px-4"></th>
                    <th className="py-2 px-4"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.asset_tag_filter} onChange={(e) => handleFilterChange('asset_tag_filter', e.target.value)} /></th>
                    <th className="py-2 px-4">
                      <Select value={filters.brand_id || undefined} onValueChange={(v) => handleFilterChange('brand_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.brands?.map(b => (<SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.modelo_filter} onChange={(e) => handleFilterChange('modelo_filter', e.target.value)} /></th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.cliente_id || undefined} onValueChange={(v) => handleFilterChange('cliente_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {formOptions.clientes?.map(c => (<SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.ip_filter} onChange={(e) => handleFilterChange('ip_filter', e.target.value)} /></th>
                    <th className="py-2 px-4">
                      <Select value={filters.status_id || undefined} onValueChange={(v) => handleFilterChange('status_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.statuses?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
                          <SelectItem value="null">Sin estado</SelectItem>
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell">
                      <Select value={filters.site_id || undefined} onValueChange={(v) => handleFilterChange('site_id', v === 'todos' ? '' : v)}>
                        <SelectTrigger className="h-7 text-xs bg-background"><SelectValue placeholder="Todos" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="todos">Todos</SelectItem>
                          {filterOptions.sites?.map(s => (<SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    </th>
                    <th className="py-2 px-4 hidden lg:table-cell"><Input placeholder="Filtrar..." className="h-7 text-xs bg-background" value={filters.location_filter} onChange={(e) => handleFilterChange('location_filter', e.target.value)} /></th>
                    <th className="py-2 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={10} className="text-center py-8 text-muted-foreground">
                        Cargando...
                      </td>
                    </tr>
                  ) : proyectores.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-8 text-muted-foreground">
                        No se encontraron proyectores
                      </td>
                    </tr>
                  ) : (
                    proyectores.map((proyector) => (
                      <tr key={proyector.id} className={`border-b border-border hover:bg-muted/50 transition-colors ${isSelected(proyector.id) ? 'bg-primary/5' : ''}`} data-testid={`proyector-row-${proyector.id}`}>
                        <td className="w-10 py-3 px-4">
                          <Checkbox
                            checked={isSelected(proyector.id)}
                            onCheckedChange={() => toggleRow(proyector.id)}
                            aria-label={`Seleccionar ${proyector.asset_tag}`}
                          />
                        </td>
                        <td className="py-3 px-4 font-mono text-sm">{proyector.asset_tag || '-'}</td>
                        <td className="py-3 px-4">{proyector.brand_name || '-'}</td>
                        <td className="py-3 px-4">{proyector.modelo || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{proyector.cliente_name || '-'}</td>
                        <td className="py-3 px-4 font-mono text-sm hidden lg:table-cell">{proyector.ip_address || '-'}</td>
                        <td className="py-3 px-4">{getStatusBadge(proyector.status_name)}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{proyector.site_name || '-'}</td>
                        <td className="py-3 px-4 hidden lg:table-cell">{proyector.location_name || '-'}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => navigate(`/micro/proyectores/${proyector.id}`)}
                              title="Ver detalle"
                              data-testid={`view-btn-${proyector.id}`}
                            >
                              <Eye className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openEditModal(proyector)}
                                title="Editar"
                                data-testid={`edit-btn-${proyector.id}`}
                              >
                                <Edit className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canCreate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => openCopyModal(proyector)}
                                title="Copiar"
                                data-testid={`copy-btn-${proyector.id}`}
                              >
                                <Copy className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive hover:text-destructive"
                                onClick={() => { setProyectorToDelete(proyector); setShowDeleteModal(true); }}
                                title="Eliminar"
                                data-testid={`delete-btn-${proyector.id}`}
                              >
                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Pagination */}
            {pagination.total > 0 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-border">
                <div className="flex items-center gap-4">
                  <p className="text-sm text-muted-foreground">
                    Mostrando {((pagination.page - 1) * pagination.limit) + 1} - {Math.min(pagination.page * pagination.limit, pagination.total)} de {pagination.total}
                  </p>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Mostrar:</span>
                    <Select
                      value={pagination.limit.toString()}
                      onValueChange={(value) => setPagination(p => ({ ...p, limit: parseInt(value), page: 1 }))}
                    >
                      <SelectTrigger className="h-8 w-20">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="25">25</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === 1}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.page === pagination.pages}
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Projector className="h-5 w-5" strokeWidth={1.5} />
                {modalMode === 'create' ? 'Nuevo Proyector' : 'Editar Proyector'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              {/* Campos únicos - borde amarillo en modo crear */}
              <div className="space-y-2">
                <Label>Asset Tag *</Label>
                <Input
                  value={formData.asset_tag || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, asset_tag: e.target.value }))}
                  placeholder="Ej: PROY-001"
                  data-validation={modalMode === 'create' && !formData.asset_tag ? 'required-empty' : ''}
                  data-testid="asset-tag-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input
                  value={formData.serial_number || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, serial_number: e.target.value }))}
                  data-validation={modalMode === 'create' && !formData.serial_number ? 'required-empty' : ''}
                  data-testid="serial-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Empresa *</Label>
                <Select value={String(formData.empresa_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, empresa_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.empresa_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.empresas?.map(e => (
                      <SelectItem key={e.id} value={String(e.id)}>{e.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Site *</Label>
                <Select value={String(formData.site_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, site_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.site_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.sites?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Marca</Label>
                <Select value={String(formData.brand_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, brand_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.brand_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.brands?.map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>{b.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Modelo</Label>
                <Input
                  value={formData.modelo || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, modelo: e.target.value }))}
                  placeholder="Ej: EB-X51, EH-TW7100"
                  data-validation={modalMode === 'create' && formData.modelo ? 'valid' : ''}
                  data-testid="modelo-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Resolución</Label>
                <Input
                  value={formData.resolution || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, resolution: e.target.value }))}
                  placeholder="Ej: 1080p, 4K, WXGA"
                  data-testid="resolution-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>IP Address</Label>
                <Input
                  value={formData.ip_address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ip_address: e.target.value }))}
                  placeholder="Ej: 192.168.1.100"
                  data-validation={modalMode === 'create' && !formData.ip_address ? 'required-empty' : ''}
                  data-testid="ip-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select value={String(formData.status_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, status_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.status_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.statuses?.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>{s.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación (FK)</Label>
                <Select value={String(formData.location_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, location_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.location_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.ubicaciones?.map(u => (
                      <SelectItem key={u.id} value={String(u.id)}>{u.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Ubicación (texto)</Label>
                <Input
                  value={formData.ubicacion || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, ubicacion: e.target.value }))}
                  placeholder="Ej: Sala de reuniones 3er piso"
                  data-testid="ubicacion-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Proveedor</Label>
                <Select value={String(formData.supplier_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, supplier_id: v }))}>
                  <SelectTrigger data-validation={modalMode === 'create' && formData.supplier_id ? 'valid' : ''}>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.proveedores?.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Cliente</Label>
                <Select value={String(formData.cliente_id || '')} onValueChange={(v) => setFormData(prev => ({ ...prev, cliente_id: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {formOptions.clientes?.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Fecha de Compra</Label>
                <Input
                  type="date"
                  value={formData.purchase_date || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, purchase_date: e.target.value }))}
                  data-testid="purchase-date-input"
                />
              </div>
              
              <div className="col-span-2 space-y-2">
                <Label>Comentarios</Label>
                <Input
                  value={formData.comments || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, comments: e.target.value }))}
                  placeholder="Observaciones adicionales..."
                  data-testid="comments-input"
                />
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSubmit} data-testid="submit-btn">
                {modalMode === 'create' ? 'Crear' : 'Guardar'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Modal */}
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <Trash2 className="h-5 w-5" strokeWidth={1.5} />
                Eliminar Proyector
              </DialogTitle>
            </DialogHeader>
            <p className="py-4">
              ¿Estás seguro de que deseas eliminar el proyector <strong>{proyectorToDelete?.asset_tag}</strong>?
              <br />
              <span className="text-sm text-muted-foreground">Esta acción no se puede deshacer.</span>
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete-btn">
                Eliminar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Edit Bar */}
        <BulkEditBar
          selectedIds={selectedIds}
          onClear={clearSelection}
          onSuccess={() => { fetchProyectores(); fetchStats(); }}
          endpoint="/assets/proyectores"
          options={{
            sites: formOptions.sites,
            empresas: formOptions.empresas,
            statuses: formOptions.statuses,
            ubicaciones: formOptions.ubicaciones
          }}
          fields={['site_id', 'empresa_id', 'status_id', 'location_id']}
        />
      </div>
    </Layout>
  );
};

export default Proyectores;
